import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators } from 'redux';
import { TFunction } from 'react-i18next';
import { Outlet, useLocation } from '@tanstack/react-router';
import {
  withAccess,
  errorBoundary,
  withInject,
  ConfigService,
} from '@piwikpro/platform';
import { RouterService } from '@piwikpro/router-crate';
import { ListSidebarPreference } from '@piwikpro/user-preferences';
import {
  SplitView,
  View,
  withTranslation,
  Sidebar,
} from '@piwikpro/ui-components';
import { Apps as AppsService } from '@piwikpro/apps-crate';
import { TrackerSettings } from '@piwikpro/tracker-settings-crate';
import { TrackerSettingsState } from '@piwikpro/tracker-settings-crate/redux/app';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import FirstAppTrialComponent from '@piwikpro/apps-crate/components/FirstAppTrial/FirstAppTrial';
import AppCreatorComponent from '@piwikpro/apps-crate/components/AppCreator/AppCreator';
import { AdministrationState, configureAppsFetch as configureAppsFetchFunction } from '../../redux';
import { SidebarLimitBanner } from '../SidebarLimitBanner/SidebarLimitBanner';
import AppsListOverlay from './AppsListOverlay';

const ASCENDING = '';
const DESCENDING = '-';
const whiteCharactersOnly = /^\s+$/i;

const mapStateToProps = (
  state: AdministrationState & { session: SessionState, trackerSettings: {app: TrackerSettingsState, global: TrackerSettingsState} },
) => ({
  data: state.administration.apps.list.apps,
  appDetails: state.administration.apps.details.settings.settings,
  appsLimit: state.organization.details.license?.current?.plan?.administration?.apps_limit,
  isLoadingList: state.administration.apps.list.isLoading || state.organization.details.isLoading,
  isLoadingDetails: state.administration.apps.details.settings.isLoading,
  filters: state.administration.apps.list.filters,
  total: state.administration.apps.list.total,
  totalWithoutFilters: state.administration.apps.list.totalWithoutFilters,
  appsListFailed: state.administration.apps.list.failed,
  appDetailsFailed: state.administration.apps.details.settings.failed,
  loggedUserEmail: state.session.email,
  appTrackerSettings: state.trackerSettings.app.settings,
  globalTrackerSettings: state.trackerSettings.global.settings,
});

const mapDispatchToProps = dispatch => ({
  configureAppsFetch: bindActionCreators(configureAppsFetchFunction, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  AppCreator: typeof AppCreatorComponent
  FirstAppTrial: typeof FirstAppTrialComponent
  apps: AppsService
  config: ConfigService
  userPreferences: any
  trackerSettings: TrackerSettings
  Title: typeof React.Component
  routerService: RouterService
}

interface Props extends InjectedProps, ReduxProps {
  filters: any
  accessManager: any
  t: TFunction
}

function AppsListElement({
  id, name, caption, onClick,
}) {
  const location = useLocation();
  const isActive = location.pathname.includes(id);
  return (
    <Sidebar.Element
      id={id}
      name={name}
      caption={caption}
      isActive={isActive}
      onClick={onClick}
    />
  );
}

class Apps extends React.Component<Props> {
  listSidebarPreference: any;

  paginationLimit: number;

  constructor(props) {
    super(props);

    const { config, userPreferences } = this.props;

    this.listSidebarPreference = new ListSidebarPreference(
      userPreferences.load(),
      '/administration/apps', {
        sortBy: config.get('APPS_DEFAULT_SORT'),
      },
    );

    this.paginationLimit = config.get('SIDEBAR_LISTS_ITEMS_PER_PAGE');
    this.moveToCreateView = this.moveToCreateView.bind(this);
    this.loadMoreListItems = this.loadMoreListItems.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.moveToDetailsView = this.moveToDetailsView.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
  }

  async componentDidMount() {
    const { apps, trackerSettings } = this.props;

    try {
      await apps.find({
        sort: {
          by: this.listSidebarPreference.sortBy,
          // @ts-ignore
          direction: this.listSidebarPreference.sortBy === 'name'
            ? ASCENDING
            : DESCENDING,
        },
        limit: this.paginationLimit,
        search: '',
        offset: undefined,
      });
      await trackerSettings.getDetails();
      // eslint-disable-next-line no-empty
    } catch { }
  }

  handleSearchChange(event) {
    const { configureAppsFetch } = this.props;

    configureAppsFetch({
      search: event.target.value,
    });
  }

  async handleSearch() {
    try {
      const { apps, filters } = this.props;

      if (!whiteCharactersOnly.test(filters.search)) {
        await apps.find({
          ...filters,
          offset: undefined,
          search: filters.search.trim(),
        });
      }
      // eslint-disable-next-line no-empty
    } catch { }
  }

  moveToCreateView() {
    const { routerService } = this.props;

    routerService.openAdministrationOverlay('/o/app-creator');
  }

  moveToDetailsView(app) {
    const { routerService } = this.props;

    routerService.push({ to: '/administration/apps/$appId', params: { appId: String(app) } });
  }

  async loadMoreListItems() {
    const { apps, filters } = this.props;

    try {
      await apps.find({ ...filters, offset: filters.offset + this.paginationLimit });
      // eslint-disable-next-line no-empty
    } catch { }
  }

  render() {
    const {
      t,
      apps,
      data,
      appDetails,
      total,
      totalWithoutFilters,
      appsLimit,
      appsListFailed,
      appDetailsFailed,
      filters,
      accessManager,
      isLoadingList,
      isLoadingDetails,
      config,
      AppCreator,
      userPreferences,
      Title,
      FirstAppTrial,
      loggedUserEmail,
      appTrackerSettings,
      globalTrackerSettings,
    } = this.props;

    const isAppsLimitReached = typeof appsLimit === 'number'
      && appsLimit >= 0
      && totalWithoutFilters >= appsLimit;
    const isOwner = accessManager.isGranted({ role: 'OWNER' });
    const isAddAppButtonDisabled = isLoadingList || !isOwner || isAppsLimitReached;
    let addAppButtonTooltip: string | undefined;
    if (!isOwner) {
      addAppButtonTooltip = t('accessControl.permissions.role.owner.required.info');
    } else if (isAppsLimitReached) {
      addAppButtonTooltip = t('apps.list.createButtonTooltip.limitReached');
    }

    const listSidebarOptions = {
      sortItems: [{
        id: 'apps-sidebar-sort-options',
        items: [
          {
            name: t('apps.list.sortOptions.name'),
            value: 'name',
            id: 'name',
          },
          {
            name: t('apps.list.sortOptions.addedAt'),
            value: 'addedAt',
            id: 'addedAt',
          },
          {
            name: t('apps.list.sortOptions.updatedAt'),
            value: 'updatedAt',
            id: 'updatedAt',
          },
        ],
      }],
      onSortByItemSelected: async (sortByValue) => {
        this.listSidebarPreference.sortBy = sortByValue;

        userPreferences.persist(this.listSidebarPreference);

        try {
          await apps.find({
            ...filters,
            sort: {
              by: this.listSidebarPreference.sortBy,
              direction: this.listSidebarPreference.sortBy === 'name'
                ? ASCENDING
                : DESCENDING,
            },
            offset: undefined,
          });
          // eslint-disable-next-line no-empty
        } catch { }
      },
      sortDefault: filters.sort.by,
    };

    const getLabelForApp = (app) => {
      let result;

      switch (filters.sort.by) {
        case 'addedAt':
          result = t('components:date.short', { date: new Date(app.attributes.addedAt) });
          break;
        case 'updatedAt':
          result = t('components:date.short', { date: new Date(app.attributes.updatedAt) });
          break;
        default:
          result = null;
          break;
      }

      return result;
    };

    return (
      <Title part={t('apps.title')}>
        <SplitView
          leftSidebar={{
            canBeHideManually: true,
            sidebar: (
              <div style={{ width: '400px', height: '100%' }}>
                <Sidebar>
                  <Sidebar.Search
                    value={filters.search}
                    placeholder={t('apps.list.searchPlaceholder')}
                    onSearchChange={this.handleSearchChange}
                    onSearch={this.handleSearch}
                    debounceTime={config.get('SIDEBAR_LISTS_SEARCH_DELAY')}
                  />
                  <Sidebar.ListOptions
                    sort={{
                      list: listSidebarOptions.sortItems,
                      onChange: listSidebarOptions.onSortByItemSelected,
                      value: this.listSidebarPreference.sortBy,
                    }}
                  />
                  {isOwner && typeof appsLimit === 'number' && appsLimit >= 0 && (
                    <SidebarLimitBanner limit={appsLimit} current={totalWithoutFilters} />
                  )}
                  {
                    !isLoadingList && data.length === 0 && (
                      <Sidebar.Empty type="subdued">
                        {t('apps.list.no-items')}
                      </Sidebar.Empty>
                    )
                  }
                  {
                    appsListFailed && (
                      <Sidebar.Empty type="error">
                        {t('ListSidebar.error')}
                      </Sidebar.Empty>
                    )
                  }
                  {isLoadingList
                    ? <Sidebar.ElementsLoading />
                    : (
                      <Sidebar.List>
                        {
                          data.map(app => (
                            <AppsListElement
                              id={app.id}
                              key={app.id}
                              name={app.attributes.name}
                              caption={getLabelForApp(app)}
                              onClick={this.moveToDetailsView}
                            />
                          ))
                        }
                      </Sidebar.List>
                    )}

                  {
                    data.length < total
                    && <Sidebar.LoadMore onClick={this.loadMoreListItems} />
                  }
                  <Sidebar.AddSection
                    mainButton={{
                      tooltip: addAppButtonTooltip ? { content: addAppButtonTooltip } : undefined,
                      onClick: this.moveToCreateView,
                      text: t('apps.list.createButton'),
                      disabled: isAddAppButtonDisabled,
                    }}
                  />
                </Sidebar>
              </div>
            ),
          }}
        >
          <View fullWidth fullHeight>
            <View.Body>
              {/* @ts-ignore */}
              <Outlet />
              <AppsListOverlay
                // @ts-ignore
                AppCreator={AppCreator}
                FirstAppTrial={FirstAppTrial}
                appCreatorProps={{
                  loggedUserEmail: loggedUserEmail!,
                  existingApp: appDetails,
                  filters,
                  appTrackerSettings,
                  globalTrackerSettings,
                }}
                firstAppTrialProps={{
                  isLoading: isLoadingDetails || isLoadingList,
                  failed: appsListFailed || appDetailsFailed,
                  app: appDetails,
                  apps: data,
                }}
              />
            </View.Body>
          </View>
        </SplitView>
      </Title>
    );
  }
}

export default connector(withInject({
  AppCreator: 'AppsCrate.components.AppCreator',
  FirstAppTrial: 'AppsCrate.components.FirstAppTrial',
  apps: 'AppsCrate.apps',
  config: 'config',
  userPreferences: 'userPreferences',
  trackerSettings: 'TrackerSettingsCrate.tracker',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
  marketing: 'MarketingCrate.marketingService',
})(
  withAccess(withTranslation('administration')(errorBoundary(Apps as any))),
));
